import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import {
  Auth,
  authState,
  User,
  GoogleAuthProvider,
  OAuthProvider,
  UserCredential,
  getAdditionalUserInfo,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
} from '@angular/fire/auth';
import { environment } from '../../../../environments/environment';
import { AnalyticsService } from '../../../services/analytics.service';
import { UserModel } from '../../../models/user.model';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Subject, takeUntil } from 'rxjs';
import * as userActions from '../../../state/actions/user.actions';
import { AppState } from '../../../state/appstate';

class MatchPasswords {
  static validate(controlName: string, checkControlName: string): ValidatorFn {
    return (controls: AbstractControl) => {
      const control = controls.get(controlName);
      const checkControl = controls.get(checkControlName);

      if (checkControl?.errors && !checkControl.errors['matching']) {
        return null;
      }

      if (control?.value !== checkControl?.value) {
        controls.get(checkControlName)?.setErrors({ matching: true });
        return { matching: true };
      } else {
        return null;
      }
    };
  }
}

export type AuthMode = 'login' | 'register' | 'forgotPassword';

// Set just before leaving for the Google/Apple sign-in page, so that on return the
// login page shows "Signing you in…" while Firebase finishes the redirect.
const SOCIAL_REDIRECT_PENDING_KEY = 'socialRedirectPending';
// A flag older than this belongs to an abandoned attempt and is ignored.
const SOCIAL_REDIRECT_MAX_AGE_MS = 10 * 60 * 1000;

// In-app browsers where Google refuses to sign in (error 403: disallowed_useragent).
const IN_APP_BROWSER_PATTERN = /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|musical_ly|TikTok|Snapchat|; wv\)/i;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  // The social buttons show "Sign in with Google/Apple" only when the row is wide enough
  // for both labels on one line; otherwise just "Google"/"Apple". Sized by the row, not
  // the viewport, because the card's width does not track the viewport.
  styles: [
    `
      .social-buttons {
        container-type: inline-size;
      }
      .sign-in-prefix {
        display: none;
      }
      @container (min-width: 384px) {
        .sign-in-prefix {
          display: inline;
        }
      }
    `,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private auth: Auth = inject(Auth);
  private logger: NGXLogger = inject(NGXLogger);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  authState$ = authState(this.auth);

  isCompletingSocialLogin: boolean = this.getPendingSocialRedirect() !== null;
  isInAppBrowser: boolean =
    typeof navigator !== 'undefined' &&
    IN_APP_BROWSER_PATTERN.test(navigator.userAgent);

  store: Store<AppState> = inject(Store<AppState>);
  router: Router = inject(Router);
  private actions$: Actions = inject(Actions);

  loginForm: FormGroup;
  registerForm: FormGroup;
  forgotPasswordForm: FormGroup;

  error: any;

  authMode: AuthMode = 'login';

  get showLoginForm(): boolean {
    return this.authMode === 'login';
  }
  set showLoginForm(value: boolean) {
    this.authMode = value ? 'login' : 'register';
  }

  resetEmailSent: boolean = false;
  resetSentEmail: string = '';
  isResetSubmitting: boolean = false;

  provider = SocialProvider;

  exit$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder, private afAuth: Auth) {
    this.loginForm = new FormGroup({
      username: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', Validators.required),
    });

    this.registerForm = this.formBuilder.group(
      {
        username: new FormControl('', [Validators.required, Validators.email]),
        password: new FormControl('', Validators.required),
        confirmPassword: new FormControl('', Validators.required),
      },
      {
        validators: [MatchPasswords.validate('password', 'confirmPassword')],
      }
    );

    this.forgotPasswordForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
    });

    this.store
      .select((state) => state.user.error)
      .pipe(takeUntil(this.exit$))
      .subscribe((error) => {
        this.error = error?.toString();
        if (this.authMode === 'forgotPassword') {
          this.isResetSubmitting = false;
        }
      });
  }

  ngOnInit(): void {
    this.completeSocialRedirect();

    if (this.router.url.includes('forgot-password')) {
      this.authMode = 'forgotPassword';
    }

    this.actions$
      .pipe(
        ofType(userActions.resetPasswordSuccess),
        takeUntil(this.exit$)
      )
      .subscribe(() => {
        this.isResetSubmitting = false;
        this.resetEmailSent = true;
        this.resetSentEmail = this.forgotPasswordForm.get('email')?.value || '';
        this.error = null;
      });

    this.actions$
      .pipe(
        ofType(userActions.resetPasswordFailure),
        takeUntil(this.exit$)
      )
      .subscribe(() => {
        this.isResetSubmitting = false;
      });

    this.authState$
      .pipe(takeUntil(this.exit$))
      .subscribe(async (fbuser: User | null) => {
        // console.log(
        //   `LoginComponent :: AuthState Email Verified: ${fbuser?.emailVerified}`
        // );
        if (fbuser) {
          if (fbuser.emailVerified) {
            this.router.navigate(['/groups']);
          } else {
            this.error =
              'Email Verification Pending. Please check your mailbox';
          }
        }
      });
  }

  // Back from Google/Apple can restore this page from the back/forward cache without
  // re-running ngOnInit, leaving "Signing you in…" up. Treat it as not completed.
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent): void {
    const pendingProvider = this.getPendingSocialRedirect();
    if (!event.persisted || !pendingProvider) return;
    this.logger.debug('login.component.ts :: Page restored from cache during redirect sign-in:', { pendingProvider });
    this.clearPendingSocialRedirect();
    this.isCompletingSocialLogin = false;
    this.store.dispatch(
      userActions.loginFailure({ error: this.noRedirectResultMessage(pendingProvider) })
    );
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  showForgotPassword() {
    this.error = null;
    this.store.dispatch(userActions.clearError());
    this.resetEmailSent = false;
    const loginEmail = this.loginForm.get('username')?.value;
    if (loginEmail) {
      this.forgotPasswordForm.patchValue({ email: loginEmail });
    }
    this.authMode = 'forgotPassword';
  }

  showLogin() {
    this.error = null;
    this.store.dispatch(userActions.clearError());
    const resetEmail = this.forgotPasswordForm.get('email')?.value;
    if (resetEmail && !this.loginForm.get('username')?.value) {
      this.loginForm.patchValue({ username: resetEmail });
    }
    this.authMode = 'login';
  }

  showRegister() {
    this.error = null;
    this.store.dispatch(userActions.clearError());
    const currentEmail =
      this.authMode === 'forgotPassword'
        ? this.forgotPasswordForm.get('email')?.value
        : this.loginForm.get('username')?.value;
    if (currentEmail && !this.registerForm.get('username')?.value) {
      this.registerForm.patchValue({ username: currentEmail });
    }
    this.authMode = 'register';
  }

  toggleRegisterForm() {
    this.error = null;
    this.store.dispatch(userActions.clearError());
    if (this.authMode === 'login') {
      this.showRegister();
    } else {
      this.showLogin();
    }
  }

  sendPasswordReset() {
    this.error = null;
    this.store.dispatch(userActions.clearError());
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }
    this.isResetSubmitting = true;
    const email = this.forgotPasswordForm.get('email')?.value?.trim();
    this.store.dispatch(userActions.resetPassword({ email }));
  }

  resendResetEmail() {
    this.sendPasswordReset();
  }

  emailLogin() {
    this.error = null;
    this.store.dispatch(userActions.clearError());
    if (this.loginForm.valid) {
      this.store.dispatch(
        userActions.emailLogin({
          username: this.loginForm.get('username')?.value,
          password: this.loginForm.get('password')?.value,
        })
      );
    }
  }

  signUp() {
    this.error = null;
    this.store.dispatch(
      userActions.signup({
        email: this.registerForm.get('username')?.value,
        password: this.registerForm.get('password')?.value,
      })
    );
  }

  async _socialLogin(loginType: SocialProvider) {
    this.logger.debug('login.component.ts :: Social login clicked:', { loginType });
    this.error = null;
    this.store.dispatch(userActions.clearError());

    let provider: GoogleAuthProvider | OAuthProvider;
    if (loginType === SocialProvider.google) {
      this.store.dispatch(userActions.googleLogin());
      const googleProvider = new GoogleAuthProvider();
      googleProvider.addScope('profile');
      googleProvider.addScope('email');
      googleProvider.addScope('openid');
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      provider = googleProvider;
    } else {
      this.store.dispatch(userActions.appleLogin());
      const appleProvider = new OAuthProvider('apple.com');
      appleProvider.addScope('email');
      appleProvider.addScope('name');
      provider = appleProvider;
    }

    if (environment.socialSignInWithPopup) {
      await this.socialLoginWithPopup(loginType, provider);
      return;
    }

    try {
      this.setPendingSocialRedirect(loginType);
      this.isCompletingSocialLogin = true;
      // Leaves the page. The result is picked up by completeSocialRedirect() when
      // Firebase sends the user back to this page.
      await signInWithRedirect(this.auth, provider);
    } catch (error: any) {
      this.logger.error('login.component.ts :: Starting social sign-in redirect failed:', { loginType, error });
      this.clearPendingSocialRedirect();
      this.isCompletingSocialLogin = false;
      this.store.dispatch(
        userActions.loginFailure({ error: this.socialLoginErrorMessage(error) })
      );
    }
  }

  /** Local and emulator builds: sign in with a popup (see environment.socialSignInWithPopup). */
  private async socialLoginWithPopup(
    loginType: SocialProvider,
    provider: GoogleAuthProvider | OAuthProvider
  ): Promise<void> {
    try {
      const credential = await signInWithPopup(this.auth, provider);
      this.logger.debug('login.component.ts :: Popup sign-in result:', {
        loginType,
        uid: credential.user.uid,
      });
      this.logSocialSignIn(credential);
      // authState$ navigates to /groups.
    } catch (error: any) {
      this.logger.error('login.component.ts :: Popup sign-in failed:', { loginType, code: error?.code, error });
      const cancelled =
        error?.code === 'auth/popup-closed-by-user' ||
        error?.code === 'auth/cancelled-popup-request';
      this.store.dispatch(
        userActions.loginFailure({
          error: cancelled
            ? this.noRedirectResultMessage(loginType)
            : this.socialLoginErrorMessage(error),
        })
      );
    }
  }

  private logSocialSignIn(credential: UserCredential): void {
    const method = credential.providerId === 'apple.com' ? 'apple' : 'google';
    if (getAdditionalUserInfo(credential)?.isNewUser) {
      this.analyticsService.logSignUp(method);
    } else {
      this.analyticsService.logLogin(method);
    }
  }

  /**
   * Picks up the outcome of a Google/Apple redirect sign-in. A successful sign-in also
   * reaches the store through onAuthStateChanged in AuthService, so this mainly
   * surfaces errors, which are only available here.
   */
  private async completeSocialRedirect(): Promise<void> {
    const pendingProvider = this.getPendingSocialRedirect();
    try {
      const credential: UserCredential | null = await getRedirectResult(this.auth);
      this.logger.debug('login.component.ts :: Redirect sign-in result:', {
        pendingProvider,
        uid: credential?.user?.uid ?? null,
        providerId: credential?.providerId ?? null,
      });
      if (credential?.user) {
        this.logSocialSignIn(credential);
        // authState$ below navigates to /groups; keep the spinner until then.
        return;
      }
      this.isCompletingSocialLogin = false;
      // We sent the user to Google/Apple but nothing came back and nobody is signed in:
      // they cancelled, or the browser lost the result. Say so instead of silently
      // showing the login form again.
      if (pendingProvider && !this.auth.currentUser) {
        this.logger.warn('login.component.ts :: Redirect sign-in returned no result:', { pendingProvider });
        this.store.dispatch(
          userActions.loginFailure({ error: this.noRedirectResultMessage(pendingProvider) })
        );
      }
    } catch (error: any) {
      this.logger.error('login.component.ts :: Redirect sign-in failed:', { code: error?.code, error });
      this.isCompletingSocialLogin = false;
      this.store.dispatch(
        userActions.loginFailure({ error: this.socialLoginErrorMessage(error) })
      );
    } finally {
      this.clearPendingSocialRedirect();
    }
  }

  private socialLoginErrorMessage(error: any): string {
    switch (error?.code) {
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method. Please sign in with that method.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      case 'auth/popup-blocked':
        return 'Your browser blocked the sign-in pop-up. Please allow pop-ups for this site and try again.';
      case 'auth/web-storage-unsupported':
        return 'Your browser is blocking storage needed to sign in. Please enable cookies or use a different browser.';
      default:
        return (error?.message ?? 'Sign-in failed. Please try again.').replace('Firebase: ', '');
    }
  }

  private noRedirectResultMessage(provider: SocialProvider): string {
    const name = provider === SocialProvider.apple ? 'Apple' : 'Google';
    return `Sign in with ${name} did not complete. If you cancelled, please try again. If this keeps happening, try a different browser or sign in with email.`;
  }

  /** The provider of a sign-in redirect started from this tab, or null if none is pending. */
  private getPendingSocialRedirect(): SocialProvider | null {
    try {
      const pending = JSON.parse(sessionStorage.getItem(SOCIAL_REDIRECT_PENDING_KEY) ?? 'null');
      const isFresh =
        !!pending?.startedAt && Date.now() - pending.startedAt < SOCIAL_REDIRECT_MAX_AGE_MS;
      return isFresh ? (pending.provider as SocialProvider) : null;
    } catch {
      return null;
    }
  }

  private setPendingSocialRedirect(provider: SocialProvider): void {
    try {
      sessionStorage.setItem(
        SOCIAL_REDIRECT_PENDING_KEY,
        JSON.stringify({ provider, startedAt: Date.now() })
      );
    } catch (error) {
      this.logger.warn('login.component.ts :: Could not store pending redirect flag:', error);
    }
  }

  private clearPendingSocialRedirect(): void {
    try {
      sessionStorage.removeItem(SOCIAL_REDIRECT_PENDING_KEY);
    } catch (error) {
      this.logger.warn('login.component.ts :: Could not clear pending redirect flag:', error);
    }
  }
}

export enum SocialProvider {
  google = 'google',
  apple = 'apple',
}
